import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { DatePickerProps } from '@verantos-dev/shared-components';
import { IndexDateTypeLegacy } from '@verantos-dev/types-cohorts';

import { useCohortEditable } from './useCohortEditable';
import {
  clearModal,
  setActiveCohortBuilderItemIndexDateType,
  setActiveCohortBuilderItemIndexDateValue,
  setIndexDate,
} from '../cohortBuilderSlice';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemIndexDateType,
  selectActiveCohortBuilderItemIndexDateValue,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemIndexDateRange,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectCohortDsl } from '../selectors/cohortDefinitionSelectors';
import { getFullCohortDslForIndexDate } from '../utils/dsl-generators/getFullCohortDslForIndexDate';

dayjs.extend(utc);

export const useIndexDateEdit = (minDate: Dayjs, maxDate: Dayjs) => {
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const { isEditable } = useCohortEditable();

  const cohortDsl = useAppSelector(selectCohortDsl);

  // select the index date and modal from the store
  const indexDateId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new Derived event
  const indexDateType = useAppSelector(selectActiveCohortBuilderItemIndexDateType) ?? '';
  const indexDateValue = useAppSelector(selectActiveCohortBuilderItemIndexDateValue);
  const indexDateRangeValue = useAppSelector(selectActiveCohortBuilderItemIndexDateRange);
  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};

  const [datePickerErrorMsg, setDatePickerErrorMsg] = useState<string | null>(null);
  const [indexDateValueAsDate, setIndexDateValueAsDate] = useState<Dayjs | null>(
    indexDateType === IndexDateTypeLegacy.CALENDAR && indexDateValue
      ? dayjs.utc(new Date(indexDateValue))
      : null
  );

  const [indexDateRangeStartValue, setIndexDateRangeStartValue] = useState<Dayjs | null>(
    indexDateRangeValue?.startDate ? dayjs.utc(new Date(indexDateRangeValue?.startDate)) : null
  );
  const [indexDateRangeEndValue, setIndexDateRangeEndValue] = useState<Dayjs | null>(
    indexDateRangeValue?.endDate ? dayjs.utc(new Date(indexDateRangeValue?.endDate)) : null
  );
  const [isDateRangeEnabled, setIsDateRangeEnabled] = useState<boolean>(
    indexDateRangeValue && indexDateRangeValue?.startDate && indexDateRangeValue?.endDate
      ? true
      : false
  );

  // Check if index date value is valid
  const indexDateValueIsValid =
    indexDateType === IndexDateTypeLegacy.CALENDAR
      ? indexDateValueAsDate && indexDateValueAsDate.isValid()
      : !!indexDateValue;

  // Check if save is allowed
  const isSaveAllowed =
    indexDateId &&
    indexDateType &&
    indexDateValueIsValid &&
    !datePickerErrorMsg &&
    (isDateRangeEnabled ? indexDateRangeStartValue && indexDateRangeEndValue : true);

  /** Click Handlers */
  // select the index type within the modal
  const onSelectTypeChange = (id: string, value: string) => {
    (value as IndexDateTypeLegacy) !== indexDateType && setIndexDateValueAsDate(null);
    value &&
      dispatch(
        setActiveCohortBuilderItemIndexDateType({
          id: indexDateId,
          type: value as IndexDateTypeLegacy,
        })
      );

    if (value !== indexDateType) {
      // clear the date value if the type changes
      setIndexDateRangeStartValue(null);
      setIndexDateRangeEndValue(null);
      setIsDateRangeEnabled(false);
      setIndexDateValueAsDate(null);
    }
  };

  // select the index date within the modal
  const onDatePickerChange = (value: Dayjs | null) => {
    setIndexDateValueAsDate(value);
    // only dispatch value update for calendar handler if the selected type is calendar
    // add the test for year < 3000 to prevent invalid dates from being saved
    if (
      indexDateType === IndexDateTypeLegacy.CALENDAR &&
      value &&
      value.isValid() &&
      value.year() < 3000
    ) {
      const calendarDate = dayjs(value); // Convert to Day.js instance
      dispatch(
        setActiveCohortBuilderItemIndexDateValue({
          id: indexDateId,
          type: indexDateType,
          indexDateValue: dayjs.utc(calendarDate).toISOString(),
        })
      );
    }
  };

  const onDatePickerError = (error: string) => {
    switch (error) {
      case 'invalidDate':
        setDatePickerErrorMsg(
          intl.formatMessage({
            id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateInvalidError',
          })
        );
        break;
      case 'minDate':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateMinDateError' },
            { minDate: minDate.format('MM/DD/YYYY') }
          )
        );
        break;
      case 'maxDate':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateMaxDateError' },
            { maxDate: maxDate.format('MM/DD/YYYY') }
          )
        );
        break;
      case 'disableFuture':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateFutureError' },
            { today: dayjs(Date()).utc().format('MM/DD/YYYY') }
          )
        );
        break;
      case null:
        setDatePickerErrorMsg('');
        break;
      default:
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateUnknownError' },
            { error }
          )
        );
    }
  };

  const onEventChange = (value: string) => {
    if (indexDateType) {
      if (value) {
        dispatch(
          setActiveCohortBuilderItemIndexDateValue({
            id: indexDateId,
            type: indexDateType,
            indexDateValue: value,
          })
        );
      } else {
        dispatch(
          setActiveCohortBuilderItemIndexDateValue({
            id: indexDateId,
            type: indexDateType,
            indexDateValue: '',
          })
        );
      }
    }
  };

  // close the modal without saving
  const onClickCancelIndexDate = () => {
    dispatch(clearModal());
  };

  // save the index date to the definition and close the modal
  const onClickModalSetIndexDate = (
    emitResearchAssistantUpdate: (nextCohortDsl: string) => void
  ) => {
    if (indexDateValue && indexDateType) {
      // only dispatch if all values are present
      const _nextIndexDate = {
        id: indexDateId,
        type: indexDateType,
        indexDateValue,
        indexDateRangeValue:
          indexDateRangeStartValue && indexDateRangeEndValue
            ? {
                startDate: indexDateRangeStartValue ? indexDateRangeStartValue.toISOString() : '',
                endDate: indexDateRangeEndValue ? indexDateRangeEndValue.toISOString() : '',
              }
            : undefined,
      };
      dispatch(setIndexDate(_nextIndexDate));

      const nextCohortDsl = getFullCohortDslForIndexDate(_nextIndexDate, cohortDsl);
      emitResearchAssistantUpdate(nextCohortDsl);
    }
  };

  const onStartDateChange = (_id: string, date: Dayjs | null) => {
    setIndexDateRangeStartValue(date);
    dispatch(
      setActiveCohortBuilderItemIndexDateValue({
        id: indexDateId,
        type: indexDateType as IndexDateTypeLegacy,
        indexDateValue: indexDateValue || '',
        indexDateRangeValue: {
          startDate: date?.isValid() ? date.toISOString() : '',
          endDate: indexDateRangeValue?.endDate || '',
        },
      })
    );
  };

  const onEndDateChange = (_id: string, date: Dayjs | null) => {
    setIndexDateRangeEndValue(date);
    dispatch(
      setActiveCohortBuilderItemIndexDateValue({
        id: indexDateId,
        type: indexDateType as IndexDateTypeLegacy,
        indexDateValue: indexDateValue || '',
        indexDateRangeValue: {
          startDate: indexDateRangeValue?.startDate || '',
          endDate: date?.isValid() ? date.toISOString() : '',
        },
      })
    );
  };

  const onClickDateRangeCheckbox = () => {
    if (isDateRangeEnabled) {
      setIndexDateRangeEndValue(null);
      setIndexDateRangeStartValue(null);
    }
    setIsDateRangeEnabled(!isDateRangeEnabled);
  };

  const baseConfigForDatePickers: Pick<
    DatePickerProps,
    'minDate' | 'maxDate' | 'disabled' | 'onError' | 'viewMode' | 'placement'
  > = {
    minDate: dayjs('1900-01-01'),
    maxDate: dayjs('2099-12-31'),
    disabled: !isDateRangeEnabled,
    viewMode: !isEditable,
    placement: 'top-end',
  };

  const indexStartDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'index-date-start-date-picker',
    value: indexDateRangeStartValue,
    onChange: onStartDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.setIndexDateDialog.startDateLabel',
    }),
  };

  const indexEndDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'index-date-end-date-picker',
    value: indexDateRangeEndValue,
    onChange: onEndDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.setIndexDateDialog.endDateLabel',
    }),
  };

  return {
    isSaveAllowed,
    indexDateId,
    indexDateType,
    indexDateValue: indexDateValue ?? null,
    indexDateValueAsDate,
    inputFieldConfigMap,
    onSelectTypeChange,
    onEventChange,
    onDatePickerChange,
    onDatePickerError,
    datePickerErrorMsg,
    onClickCancelIndexDate,
    onClickModalSetIndexDate,
    isEditable,
    indexStartDatePicker,
    indexEndDatePicker,
    isDateRangeEnabled,
    onClickDateRangeCheckbox,
  };
};
